import type {
  ContradictionFinding,
  OasisValidationError,
} from '../../../services/api/types.gen';
import { ACTIVE_DIAGNOSIS_CATEGORY } from '../OASISFormLabels';
import { categoriesForContradictionLocation } from './contradictionNavigation';

/**
 * Every scrubber tab, with the copy the panel renders for it, so adding a tab is
 * one edit here rather than three fall-through ternaries in OASISForm.
 *
 * `section` / `misc` / `all` are clinician-facing CMS filters; `qa` is a separate
 * audience carrying QA findings only. The two sets never mix (EMR-809). Order is
 * render order — the CMS trio stays together, with QA last.
 *
 * `label: null` means "use the open section's name".
 */
export const SCRUBBER_TAB_DEFINITIONS = [
  { id: 'section', label: null, emptyMessage: 'No issues in section' },
  { id: 'misc', label: 'Misc', emptyMessage: 'No issues in Misc' },
  { id: 'all', label: 'All CMS', emptyMessage: 'No issues' },
  { id: 'qa', label: 'QA', emptyMessage: 'No QA issues' },
] as const;

export type ScrubberTab = (typeof SCRUBBER_TAB_DEFINITIONS)[number]['id'];

export const SCRUBBER_TABS: readonly ScrubberTab[] =
  SCRUBBER_TAB_DEFINITIONS.map(definition => definition.id);

export type ScrubberCmsItem = {
  kind: 'cms';
  error: OasisValidationError;
};

export type ScrubberContradictionItem = {
  kind: 'contradiction';
  finding: ContradictionFinding;
};

export type ScrubberListItem = ScrubberCmsItem | ScrubberContradictionItem;

function uniqueCategories(categories: string[]): string[] {
  return Array.from(new Set(categories.filter(Boolean)));
}

/**
 * Diagnosis severity errors (EMR-1280) address rows in the diagnosis table, not
 * a question, so they carry no qid to map. They belong to Active Diagnosis --
 * the same section `sectionIdForContradictionSource` gives the QA path for
 * `diagnosis_list`. Routing them here rather than at each call site keeps the
 * tab filter, the sidebar fatal count and the Misc bucket consistent, since all
 * three read this function.
 */
const DIAGNOSIS_SEVERITY_EDIT_ID_PREFIX = 'dx-severity-';

export function cmsErrorIsDiagnosisSeverity(
  err: Pick<OasisValidationError, 'edit_id'>
): boolean {
  return (err.edit_id || '').startsWith(DIAGNOSIS_SEVERITY_EDIT_ID_PREFIX);
}

export function categoriesForCmsError(
  err: Pick<OasisValidationError, 'qids' | 'edit_id'>,
  qidToCategoryMap: Record<string, string>
): string[] {
  if (cmsErrorIsDiagnosisSeverity(err)) {
    return [ACTIVE_DIAGNOSIS_CATEGORY];
  }
  return uniqueCategories(
    (err.qids || []).map(qid => qidToCategoryMap[qid]).filter(Boolean)
  );
}

export function categoriesForContradiction(
  finding: ContradictionFinding,
  qidToCategoryMap: Record<string, string>
): string[] {
  const categories: string[] = [];
  for (const location of finding.error_locations || []) {
    categories.push(
      ...categoriesForContradictionLocation(location, qidToCategoryMap)
    );
  }
  return uniqueCategories(categories);
}

function cmsErrorMatchesTab(
  categories: string[],
  tab: ScrubberTab,
  currentCategory: string | null
): boolean {
  if (tab === 'qa') return false;
  if (tab === 'all') return true;
  if (tab === 'misc') return categories.length === 0;
  return categories.includes(currentCategory || '');
}

/** QA findings live only on the QA tab — never on Section/Misc/All. */
function contradictionMatchesTab(tab: ScrubberTab): boolean {
  return tab === 'qa';
}

/** FATAL_MODE body: section-filtered fatals only. */
export function buildFatalScrubberErrors(
  errors: OasisValidationError[],
  tab: ScrubberTab,
  currentCategory: string | null,
  qidToCategoryMap: Record<string, string>
): OasisValidationError[] {
  return errors.filter(err => {
    if (err.severity !== 'fatal') return false;
    return cmsErrorMatchesTab(
      categoriesForCmsError(err, qidToCategoryMap),
      tab,
      currentCategory
    );
  });
}

/**
 * Advisory scrubber body. On Section/Misc/All this is CMS warnings only,
 * section-filtered, with unmapped warnings landing in Misc. On QA it is every QA
 * finding, unfiltered by section — the cards carry their own section chips, and
 * the sidebar dots show where each one lives.
 */
export function buildAdvisoryScrubberItems(
  warnings: OasisValidationError[],
  contradictions: ContradictionFinding[],
  tab: ScrubberTab,
  currentCategory: string | null,
  qidToCategoryMap: Record<string, string>
): ScrubberListItem[] {
  const items: ScrubberListItem[] = [];

  for (const error of warnings) {
    if (error.severity !== 'warning') continue;
    if (
      !cmsErrorMatchesTab(
        categoriesForCmsError(error, qidToCategoryMap),
        tab,
        currentCategory
      )
    ) {
      continue;
    }
    items.push({ kind: 'cms', error });
  }

  if (contradictionMatchesTab(tab)) {
    // Dismissed findings stay in the list but sink below the active ones
    // (EMR-1145) — they render faded, and only `countActiveScrubberItems`
    // decides what the badges say.
    for (const finding of contradictions) {
      if (!finding.dismissed) items.push({ kind: 'contradiction', finding });
    }
    for (const finding of contradictions) {
      if (finding.dismissed) items.push({ kind: 'contradiction', finding });
    }
  }

  return items;
}

/**
 * How many of these items count against the visit. Dismissed QA findings are
 * still rendered, so list length is not the badge number (EMR-1145).
 */
export function countActiveScrubberItems(items: ScrubberListItem[]): number {
  return items.filter(
    item => item.kind !== 'contradiction' || !item.finding.dismissed
  ).length;
}

function countByCategory(
  categoriesPerItem: string[][]
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const categories of categoriesPerItem) {
    for (const sec of categories) {
      counts[sec] = (counts[sec] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Sidebar advisory counts while a CMS tab is selected: CMS warnings only.
 * QA findings are counted separately so the dots can follow the active tab.
 */
export function countCmsWarningsByCategory(
  warnings: OasisValidationError[],
  qidToCategoryMap: Record<string, string>
): Record<string, number> {
  return countByCategory(
    warnings
      .filter(error => error.severity === 'warning')
      .map(error => categoriesForCmsError(error, qidToCategoryMap))
  );
}

/**
 * Sidebar advisory counts while the QA tab is selected: QA findings only, and
 * only ones that still count — a dismissed finding must not keep a sidebar dot
 * lit (EMR-1145).
 */
export function countQaFindingsByCategory(
  contradictions: ContradictionFinding[],
  qidToCategoryMap: Record<string, string>
): Record<string, number> {
  return countByCategory(
    contradictions
      .filter(finding => !finding.dismissed)
      .map(finding => categoriesForContradiction(finding, qidToCategoryMap))
  );
}

/** Sidebar fatal counts: CMS fatals only. */
export function countFatalsByCategory(
  errors: OasisValidationError[],
  qidToCategoryMap: Record<string, string>
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const error of errors) {
    if (error.severity !== 'fatal') continue;
    for (const sec of categoriesForCmsError(error, qidToCategoryMap)) {
      counts[sec] = (counts[sec] || 0) + 1;
    }
  }
  return counts;
}
